import React from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { submissionValidationSchema } from "./Validation";
import {
  Button,
  TextField,
  Grid,
  Container,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NumberFormat from "react-number-format";
import { Alert } from '@material-ui/lab';

const stylesFunc = makeStyles((theme) => ({
  root: {
    '& input:valid + fieldset': { borderColor: 'green', borderWidth: 1 },
    '& input:invalid:focus + fieldset': { borderColor: 'red', borderWidth: 1 },
    '& input:valid:focus + fieldset': { borderLeftWidth: 6, padding: '4px !important' },
  },
  wrapper: {
    marginTop: "3rem",
    height: "calc(200vh - 19.0625rem)",
    textAlign: "start",
    marginBottom: "9rem",
  },
  formHeader: { textAlign: "center", marginBottom: "1rem", marginTop: "1rem" },
  header: { display: "flex", flexDirection: "column", alignItems: "center" },
  reviewButton: {
    marginTop: "2rem",
    marginBottom: "1em",
    background: "#14688D",
    color: "#fff",
    "&:hover": { backgroundColor: "#fff", color: "#14688D" },
  },
  noticeCard: {
    borderRadius: 14,
    border: '1px solid rgba(255, 152, 0, 0.25)', // softer orange border
    background:
      'linear-gradient(145deg, rgba(255,243,224,0.95) 0%, rgba(255,224,178,0.95) 100%)', // light orange gradient
    boxShadow: '0 8px 20px rgba(255, 152, 0, 0.15)', // subtle orange glow
    padding: theme.spacing(2.5),
  },
  
  noticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: theme.spacing(1),
  },
  noticeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 8,
    background: '#14688D',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
  },
  noticeTitle: {
    color: '#14688D',
    fontWeight: 700,
    margin: 0,
    fontSize: 16,
  },
  noticeBody: {
    color: '#34455E',
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },
  noticeEmphasis: {
    color: '#d32f2f',
    fontWeight: 700,
    margin: 0,
    fontSize: 16,
  },
}));

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      prefix={"$ "}
      // thousandSeparator
    />
  );
}

const FormIncome = ({ setForm, inputData, navigation }) => {
  const {
    salary,
    stipend,
    federal,
    incomeFromstate,
    university,
    other,
    zelle,
    bankRouting,
    bankAccount,
    acknowledge,
  } = inputData;
  const formStyles = stylesFunc();
  const { previous, next } = navigation;

  // base required fields for this step
  const baseIncome = submissionValidationSchema.pick([
    "salary",
    "stipend",
    "federal",
    "incomeFromstate",
    "university",
    "other",
    "acknowledge",
  ]);

  // object-level rule: require either zelle OR (bankRouting && bankAccount)
  const payoutRule = Yup.object().shape({
    zelle: Yup.string().nullable(),
    bankRouting: Yup.string().nullable(),
    bankAccount: Yup.string().nullable(),
  }).test("payout-one-of", "Provide either Zelle OR bank routing & account.", function (values) {
    const v = values || this.parent || {};
    const hasZelle = typeof v.zelle === "string" && v.zelle.trim() !== "";
    const hasBank =
      typeof v.bankRouting === "string" && v.bankRouting.trim() !== "" &&
      typeof v.bankAccount === "string" && v.bankAccount.trim() !== "";
    if (hasZelle || hasBank) return true;
    // attach the error to zelle so it's visible under that field
    return this.createError({ path: "zelle", message: "Provide either Zelle OR bank routing & account." });
  });

  const incomeSchema = baseIncome.concat(payoutRule);

  return (
    <Container className={formStyles.wrapper} maxWidth="sm" style={{ position: 'relative', zIndex: 2 }}>
      <div className={formStyles.header}>
        <i className="fas fa-user-graduate fa-5x" style={{ color: "#14688D" }}></i>
        <Typography className={formStyles.formHeader} style={{ color: "#34455E" }} variant="h4">
          Scholarship Program Application
        </Typography>
        <Typography className={formStyles.formHeader} style={{ color: "#34455E" }} variant="h5">
          Income ($)
        </Typography>
      </div>
      <Formik
        initialValues={inputData}
        enableReinitialize
        validationSchema={incomeSchema}
        validateOnMount
        onSubmit={(values) => {
          Object.entries(values).forEach(([name, value]) =>
            setForm({ target: { name, value } })
          );
          next();
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit} className={formStyles.root}>
            <Grid container spacing={2} justify="center">
              <Grid item xs={12}>
                <Typography variant="subtitle1" display="block" component="h1">
                  Wage (Numbers Only)
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  type="decimal"
                  name="salary"
                  label="Salary"
                  variant="outlined"
                  fullWidth
                  value={salary}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.salary && formik.errors.salary)}
                  helperText={(formik.touched.salary && formik.errors.salary) || (salary === "" ? "Monthly salary" : null)}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  required
                  type="decimal"
                  name="stipend"
                  label="Stipend"
                  variant="outlined"
                  fullWidth
                  value={stipend}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.stipend && formik.errors.stipend)}
                  helperText={(formik.touched.stipend && formik.errors.stipend) || (stipend === "" ? "Stipend for the semester" : null)}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" display="block" component="h1">
                  Grants (Numbers Only)
                </Typography>
              </Grid>

              <Grid item xs={4}>
                <TextField
                  required
                  type="decimal"
                  name="federal"
                  label="Federal"
                  variant="outlined"
                  fullWidth
                  value={federal}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.federal && formik.errors.federal)}
                  helperText={(formik.touched.federal && formik.errors.federal) || (federal === "" ? "Federal grant(s) for the semester" : null)}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  required
                  type="decimal"
                  name="incomeFromstate"
                  label="State"
                  variant="outlined"
                  fullWidth
                  value={incomeFromstate}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.incomeFromstate && formik.errors.incomeFromstate)}
                  helperText={(formik.touched.incomeFromstate && formik.errors.incomeFromstate) || (incomeFromstate === "" ? "State grant(s) for the semester" : null)}
                />
              </Grid>

              <Grid item xs={4}>
                <TextField
                  required
                  type="decimal"
                  name="university"
                  label="University"
                  variant="outlined"
                  fullWidth
                  value={university}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.university && formik.errors.university)}
                  helperText={(formik.touched.university && formik.errors.university) || (university === "" ? "University grant(s) for the semester" : null)}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" display="block" component="h1">
                  Other Income (Numbers Only)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  required
                  type="decimal"
                  name="other"
                  label="Other"
                  variant="outlined"
                  fullWidth
                  value={other}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  InputProps={{ inputComponent: NumberFormatCustom }}
                  error={Boolean(formik.touched.other && formik.errors.other)}
                  helperText={(formik.touched.other && formik.errors.other) || (other === "" ? "Monthly other income(s)" : null)}
                />
              </Grid>
 

<Grid item md={12}>
  <div className={formStyles.noticeCard}>
    <div className={formStyles.noticeHeader}>
      <span className={formStyles.noticeBadge}>!</span>
      <Typography variant="subtitle1" className={formStyles.noticeTitle}>
      Important Payment Note
      </Typography>
    </div>

    <Typography variant="body1" className={formStyles.noticeBody}>
    • Banks may place Zelle transfers on temporary hold for fraud review, which can cause delays.<br></br>
    • Transfers from Blue Tulip’s Bank of America account to other banks incur fee per transaction.
    </Typography>
<br></br>
    <Typography variant="body1" className={formStyles.noticeEmphasis}>
    Providing Bank of America account details (if you have one) can help avoid both delays and fees, in case you are found eligible for the scholarship.
    </Typography>

    
  </div>
</Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" display="block" component="h1">
                  Payout Details (Provide either Zelle OR bank routing & account)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="text"
                  name="zelle"
                  label="Zelle (phone or email)"
                  variant="outlined"
                  fullWidth
                  value={zelle}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.zelle && formik.errors.zelle)}
                  helperText={formik.touched.zelle && formik.errors.zelle ? formik.errors.zelle : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" display="block" component="div" style={{ color: "#54606c" }}>
                  OR provide banking information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="text"
                  name="bankRouting"
                  label="Routing Number"
                  variant="outlined"
                  fullWidth
                  value={bankRouting}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.bankRouting && formik.errors.bankRouting)}
                  helperText=""
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  type="text"
                  name="bankAccount"
                  label="Bank Account Number"
                  variant="outlined"
                  fullWidth
                  value={bankAccount}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.bankAccount && formik.errors.bankAccount)}
                  helperText=""
                />
              </Grid>
            
                <Grid item xs={12}>
                <Typography variant="subtitle1" display="block" component="h1">
                Please write your full name in below box to acknowledge the terms and conditions of the Blue Tulip Scholarship grant. 
                </Typography>
                <br></br>
                <TextField
                  required
                  type="text"
                  name="acknowledge"
                  label="First Name and Last Name"
                  variant="outlined"
                  fullWidth
                  value={acknowledge}
                  onChange={(e) => { formik.handleChange(e); setForm(e); }}
                  onBlur={formik.handleBlur}
                  error={Boolean(formik.touched.acknowledge && formik.errors.acknowledge)}
                  helperText={
                    (formik.touched.acknowledge && formik.errors.acknowledge) ||
                    (acknowledge === "" ? "Enter your first name and last name" : null)
                  }
                />
              </Grid>

              <Grid item xs={4}>
                <Button
                  className={formStyles.reviewButton}
                  type="button"
                  variant="contained"
                  fullWidth
                  onClick={previous}
                >
                  Back
                </Button>
              </Grid>

              <Grid item xs={4}>
                <Button
                  className={formStyles.reviewButton}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  Review
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default FormIncome;
